/**
 * Format: Utils
 * ------------------------------------------------------------------------------
 * - Shared functions between formatters.
 *
 */
import { getProductTagValueByKey } from '~/helpers/product'
import { stringToHandle, stringToTitleCase } from '~/helpers/strings'
import { getCloudinaryBaseUrl, getCloudinarySizedImageUrl } from '~/helpers/format'

/**
 * Return
 * @param {Array} tags - Shopify product tags.
 * @returns {Array|false} - Array of badge objects for valid tags, or false if not.
 */
export function getFormattedBadges(tags) {
  const badgeTag = getProductTagValueByKey(tags, 'badge', false)

  if (!badgeTag) {
    return false
  }

  const badgeValues = Array.isArray(badgeTag) ? badgeTag : [badgeTag]
  const badgeHandles = badgeValues.map(badgeValue => ({
    handle: stringToHandle(badgeValue),
    name: badgeValue,
  }))

  const predefinedBadgeValues = [
    'MINT',
    'COMING SOON',
    'MV LIMITED',
    'MINT STUDIO',
  ].map((badge) => stringToHandle(badge))

  const validBadgeHandles = badgeHandles.filter(badge => predefinedBadgeValues.includes(badge.handle))

  if (!validBadgeHandles.length) {
    return false
  }

  return validBadgeHandles.map(badge => {
    return {
      icon: badge.handle,
      name: badge.name,
    }
  })
}

/**
* Format the labels for the PLP.
* @param {Array} tags - Shopify product tags
* @returns {Boolean|String}
*/
export function getFormattedLabels(tags) {
  const label = getProductTagValueByKey(tags, 'label_under', true)

  if (!label) {
    return false
  }

  return stringToTitleCase(label)
}

/**
 * Format the garment style properties.
 * @param {Array} tags - Shopify product tags.
 * @returns {Array}
 */
export function getFormattedStyles(tags) {
  const styleKeys = [
    'fabric',
    'fit',
    'heel_height',
    'neckline',
    'rise',
    'sleeve_length',
    'wearer_length',
  ]

  const styleTags = styleKeys.map((style) => {
    const styleValue = getProductTagValueByKey(tags, style, true)

    if (!styleValue) {
      return false
    }

    const styleKey = style.replace(/_/g, ' ')

    return {
      name: stringToTitleCase(styleKey),
      value: stringToTitleCase(styleValue),
    }
  }).filter(Boolean)

  return styleTags
}

/**
 *
 * - https://mint-velvet.atlassian.net/wiki/spaces/TECHNOLOGY/pages/28180481/Trending+Products+Cards
 * @param {Object} product - Formatted Shopify product object.
 * @returns {Array}
 */
export function getFormattedTrendingBanners(product) {

  /**
   * If we're not on a Collection, return empty as the
   * data below isn't going to be needed.
   */
  if (mint.request.pageType !== 'collection') return false

  /*
  * Check if trendingData actually exists on this page.
  */

  let trendingData
  if (mint.collection.trendingData) {
    trendingData = [...mint.collection.trendingData]
  }

  if (!trendingData) return false

  /*
  * Quick return if product has no associated trendingData
  */
  const productTrendingData = trendingData.find(td => td.product == product.handle)
  if (!productTrendingData) return false

  const returnData = { ...productTrendingData }

  if (productTrendingData.image)
    returnData.media_type = 'image'

  if (productTrendingData.video)
    returnData.media_type = 'video'

  returnData.alt = `${product.title} 'Trending' Banner.`

  return returnData
}

/**
 * Returns product types.
 * @param {Object} product - Shopify product object.
 * @returns {Object}
 */
export function getTypesFromMetafields(product) {

  const nsCategory = product?.metaNSCategory?.value ?
    product.metaNSCategory.value : undefined

  return { nsCategory }
}

/**
 * Returns product types.
 * @param {Array} tags - Shopify product tags.
 * @returns {Object}
 */
export function getFormattedTypes(tags) {
  const endUse = getProductTagValueByKey(tags, 'end_use', true) || false
  const range = getProductTagValueByKey(tags, 'range', true) || false
  const shopify = getProductTagValueByKey(tags, 'product_type', true) || false
  const specific = getProductTagValueByKey(tags, 'more_specific', true) || false
  const sustainable = getProductTagValueByKey(tags, 'sustainable', true) || false
  let story = getProductTagValueByKey(tags, 'story', true) || false
  if (story && story.toLowerCase().includes('not_in_a_story')) {
    story = false
  }

  return {
    endUse: stringToTitleCase(endUse) || false,
    range: stringToTitleCase(range) || false,
    shopify: stringToTitleCase(shopify) || false,
    specific: stringToTitleCase(specific) || false,
    story: stringToTitleCase(story) || false,
    sustainable: stringToTitleCase(sustainable) || false,
  }
}

/**
 * Format the Wishlist data.
 * - Local component will listen for Swym EventBus event and update this object.
 * - Listen out for Swym EventBus events in component and
 *   update `this.product.wishlist` accordingly.
 * @param {Array} tags - Shopify product tags.
 * @returns {Object}
 */
export function getFormattedWishlist(tags) {
  if (!tags) {
    return false
  }

  const bisEnabled = Boolean(!tags.find((tag) => tag === 'swym-disabled'))

  return {
    added: false,
    bisEnabled,
    wishlistEnabled: false,
  }
}

/**
 * Define if a product is a web exclusive or not.
 * - Local component will listen for Swym EventBus event and update this object.
 * @param {Array} tags - Shopify product tags.
 * @returns {Boolean}
 */
export function getFormattedWebExclusive(tags) {
  if (!tags) {
    return false
  }

  let exclusiveTag = getProductTagValueByKey(tags, 'exclusive')

  if (Array.isArray(exclusiveTag)) {
    exclusiveTag = exclusiveTag[0]
  }

  if (!exclusiveTag) {
    return false
  }

  return exclusiveTag.toLowerCase() === 'yes' ? true : false
}

/**
 * Return formatted images hosted by Cloudinary.
 * @param {Object} response - Attraqt product response.
 * @param {String} productTitle - Product Title
 * @returns {Boolean|Array}
 */
export function getFormattedCloudinaryImages(imageAttribute, productTitle) {
  if (!imageAttribute) {
    return false
  }

  // Double string parse
  const images = imageAttribute.map((details, index) => {
    const baseUrl = getCloudinaryBaseUrl()
    const src = getCloudinarySizedImageUrl({ baseUrl: baseUrl, publicId: details.public_id })

    return {
      alt: `${productTitle} (Image ${index}).`,
      baseUrl: baseUrl,
      publicId: details.public_id,
      source: 'cloudinary',
      src: src,
    }
  })

  return images
}

/**
 * Return an appropriate size or length value based on the user's locale.
 * @param {String} value - The size or length value to be converted, e.g. "08".
 * @param {Array} internationalSizes - The product metafield containing matching
 *   international sizing values for various locales.
 */
export function getInternationalSizing(value, internationalSizes) {
  if (
    !value ||
    !internationalSizes?.length ||
    !mint.flags?.international?.showLocalisedSizes
  ) {
    return false
  }

  const { continentCode, country } = mint.shop

  let userLocale = country

  /**
   * By default, the user's country is used for sizing. An exception is made if
   * the user is located in Europe, but not within the UK or Ireland. This
   * indicates that EU sizes should be used instead.
   */
  if (
    continentCode === 'EU' &&
    country !== 'GB' &&
    country !== 'IE'
  ) {
    userLocale = continentCode
  }

  /**
   * Find an international sizing value that matches the "base" sizing value.
   */
  const sizeMatch = internationalSizes.find(size => {
    return size.value === value
  })

  if (!sizeMatch) {
    return false
  }

  /**
   * For the matching sizing value, look for an appropriate locale.
   */
  const localeMatch = sizeMatch.locales.find(locale => {
    return locale.countryCode.includes(userLocale)
  })

  if (!localeMatch) {
    return false
  }

  return localeMatch.value
}

/**
   * Return the product image source, correcting the CDN URL if necessary
   * @param {string} src - product image src.
   * @returns {Boolean|String}
   *
   * For regex reference:
   * - Example oldCdn Url:
   *   - https://cdn.shopify.com/s/files/1/0568/3632/5535/files/product-name-701280_620x.jpg?v=655685
   *   - https://cdn.shopify.com/s/files/1/0568/3632/5535/products/product-name-701280_620x.jpg?v=655685
   * - Example newCdn Url: `//www.mintvelvet.co.uk/cdn/shop/products/product-name-701280_620x.jpg?v=655685`
   */
export function updateProductImageCDN(src) {
  if (!src) {
    return false
  }

  const legacyCdnFormat = src.startsWith('https://cdn.shopify.com')

  if (!legacyCdnFormat) {
    return src
  }

  /**
   * Regex matches either /products/ or /files/ followed by anything until `?`
   * For example, legacy cdn url may or may not include a '/products/'
   * subfolder for the current image source
   * @problem Resolves extracting the type (files or products) and filename
   * from URLs.
   */
  const regex = /\/(files|products)\/([^/?]+)(?:\?|$)/

  if (!src.match(regex)) {
    return false
  }

  const type =
    src.match(regex)[1] === 'products'
      ? 'products'
      : 'files'

  const imageURL = mint.urls[type].replace(
    'placeholder',
    src.match(regex)[2],
  )

  return imageURL
}
